/**
 * What the sending bank needs in order to deliver the money.
 *
 * ## The product fact this module encodes
 *
 * **The sending institution decides which recipient details it requires.** One
 * bank needs a name and an IBAN; another needs a name, a phone number and an
 * account number. Nura does not get to pick, and Nura must not ship one
 * universal recipient form with a bank's requirements bolted on afterwards.
 *
 * So requirements are **configuration**, read by one renderer. There is no
 * branch anywhere in this codebase of the form `if (tenant === "qasr")`, and
 * `frontend/test/recipient-requirements.test.ts` asserts that a tenant's field
 * set can be changed with no change to the form code at all.
 *
 * ## The vocabulary is closed, and that is the security control
 *
 * Four field names exist. A tenant selects from them; a tenant cannot define a
 * fifth, cannot supply a label, an input type, an `autocomplete` value or a
 * validation rule, and therefore cannot introduce a field that asks for
 * something it should not. `CLAUDE.md` § Privacy forbids Nura components from
 * requesting bank credentials at all, and the reliable way to honour that is to
 * make a credential field **unrepresentable** rather than to review each new
 * form for one. A `RecipientFieldName` of `"password"` does not type-check, and
 * the `receiver-claim-boundary` gate fails the build if the strings appear.
 *
 * ## Payout information is not a credential
 *
 * The distinction this module rests on:
 *
 * - **Recipient payout information** — a name, an IBAN, an account number, a
 *   phone number used as a payout destination. The sending bank legitimately
 *   requires it to move money, and `CLAUDE.md` § Receiver data modes governs how
 *   it is collected and by whom.
 * - **Bank authentication credentials** — a username, a password, a PIN, an OTP,
 *   a biometric, a card PIN, a security answer, an access token. Nura never
 *   collects any of these, in any surface, for any reason.
 *
 * The first is configurable here. The second is not expressible here.
 *
 * ## What validation means, and what it does not
 *
 * Validation is **format assistance for a person typing on a phone**, nothing
 * more. It catches a transposed character before a form is submitted; it does
 * not decide whether a destination is real, reachable, or the person's own. The
 * bank remains the authority for that — `CLAUDE.md` § Product boundary — and no
 * checksum is computed here precisely so that no one reads a green field as
 * Nura having verified anything.
 */

/**
 * Every recipient field this product can ask for. Closed on purpose.
 *
 * Adding a member is a product and privacy decision, not an implementation
 * detail: it changes what a Nura surface is capable of asking a person for.
 */
export type RecipientFieldName = "fullName" | "iban" | "accountNumber" | "phoneNumber";

export const RECIPIENT_FIELD_NAMES: readonly RecipientFieldName[] = [
  "fullName",
  "iban",
  "accountNumber",
  "phoneNumber",
];

/**
 * The UI properties a field carries.
 *
 * Only what the interface currently needs. Deliberately not an open schema
 * system and deliberately not a metadata bag — both would let a field carry
 * behaviour nobody reviewed.
 */
export interface RecipientFieldDefinition {
  readonly name: RecipientFieldName;
  readonly label: string;
  /** One short line under the control. Never an example that looks like real data. */
  readonly help: string;
  readonly type: "text" | "tel";
  readonly inputMode: "text" | "tel" | "numeric";
  /**
   * Browser autofill hint, where one is both safe and useful.
   *
   * Present for a name and a phone number, which a person's device already holds
   * and which autofill handles well. **Absent for the IBAN and the account
   * number**: the useful autofill tokens for those are payment-instrument
   * tokens, and asking a browser to treat a payout destination as a stored
   * payment credential is the wrong instruction to give it.
   */
  readonly autocomplete?: "name" | "tel";
  /** Format assistance only. Returns the message to show, or `undefined`. */
  validate(value: string): string | undefined;
}

/** Collapse the spacing people type into grouped identifiers. */
function compact(value: string): string {
  return value.replace(/[\s -]/g, "");
}

const DEFINITIONS: Readonly<Record<RecipientFieldName, RecipientFieldDefinition>> = {
  fullName: {
    name: "fullName",
    label: "Full name",
    help: "As it appears on the receiving account.",
    type: "text",
    inputMode: "text",
    autocomplete: "name",
    validate(value: string): string | undefined {
      const trimmed = value.trim();
      if (trimmed.length < 2 || !/\p{L}/u.test(trimmed)) {
        return "Enter the name on the receiving account.";
      }
      return undefined;
    },
  },

  iban: {
    name: "iban",
    label: "IBAN",
    help: "Two letters, two digits, then the rest of the number.",
    type: "text",
    inputMode: "text",
    validate(value: string): string | undefined {
      // Structure only — two letters, two check digits, then the national part.
      // No checksum is computed: the bank validates the destination, and a form
      // that appeared to do so would be claiming an authority Nura does not have.
      if (!/^[A-Za-z]{2}\d{2}[A-Za-z0-9]{10,30}$/.test(compact(value))) {
        return "Enter an IBAN — two letters, two digits, then the rest.";
      }
      return undefined;
    },
  },

  accountNumber: {
    name: "accountNumber",
    label: "Account number",
    help: "Digits only, as your bank shows it.",
    type: "text",
    inputMode: "numeric",
    validate(value: string): string | undefined {
      if (!/^\d{6,17}$/.test(compact(value))) {
        return "Enter the account number, digits only.";
      }
      return undefined;
    },
  },

  phoneNumber: {
    name: "phoneNumber",
    label: "Phone number",
    help: "Include the country code.",
    type: "tel",
    inputMode: "tel",
    autocomplete: "tel",
    validate(value: string): string | undefined {
      if (!/^\+?\d{6,15}$/.test(compact(value.replace(/[()]/g, "")))) {
        return "Enter a phone number, including the country code.";
      }
      return undefined;
    },
  },
};

export function recipientField(name: RecipientFieldName): RecipientFieldDefinition {
  return DEFINITIONS[name];
}

/** One field a tenant asks for, and whether it insists on it. */
export interface RecipientFieldRequirement {
  readonly name: RecipientFieldName;
  readonly required: boolean;
}

/** Everything the sending institution needs before it can deliver the money. */
export interface RecipientRequirements {
  readonly fields: readonly RecipientFieldRequirement[];
}

function required(...names: readonly RecipientFieldName[]): RecipientRequirements {
  return { fields: names.map((name) => ({ name, required: true })) };
}

/**
 * The synthetic requirement sets the reference tenants select from.
 *
 * **Fictional reference configurations.** They demonstrate that institutions
 * differ; they are not a claim about what any real bank asks for, and no real
 * institution's requirements are represented here.
 *
 * Tenants reference a set by name rather than spelling out field names in
 * `tenants.ts`, which keeps the rail-specific vocabulary in this one reviewed
 * file — see the narrow, path-anchored allowance in the `prohibited-schema`
 * gate.
 */
export const RECIPIENT_REQUIREMENT_SETS = {
  /** A name and an IBAN. The common European shape. */
  nameAndIban: required("fullName", "iban"),
  /** A name, a phone number and an account number. */
  namePhoneAndAccount: required("fullName", "phoneNumber", "accountNumber"),
} as const satisfies Readonly<Record<string, RecipientRequirements>>;

/** Raised when a requirement set cannot be honoured. */
export class RecipientRequirementsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RecipientRequirementsError";
  }
}

/**
 * Validate a requirement set.
 *
 * Fails loudly rather than rendering something surprising: an unknown field name
 * has no definition and therefore no label, no keyboard and no validation, and a
 * duplicate would put the same question on screen twice.
 */
export function assertValidRequirements(requirements: RecipientRequirements): void {
  const seen = new Set<RecipientFieldName>();
  for (const requirement of requirements.fields) {
    if (!RECIPIENT_FIELD_NAMES.includes(requirement.name)) {
      throw new RecipientRequirementsError(`${requirement.name} is not an approved field`);
    }
    if (seen.has(requirement.name)) {
      throw new RecipientRequirementsError(`${requirement.name} is required twice`);
    }
    seen.add(requirement.name);
  }
  if (requirements.fields.length === 0) {
    throw new RecipientRequirementsError("a requirement set must contain at least one field");
  }
}

/** The values a person entered, keyed by approved field name. */
export type RecipientValues = ReadonlyMap<RecipientFieldName, string>;

/**
 * Every required field carries a value that passes its own format check.
 *
 * Used by the flow as well as the form. The interface should never present an
 * incomplete submission, and the state machine should not depend on the
 * interface having been careful.
 */
export function missingRequirements(
  requirements: RecipientRequirements,
  values: RecipientValues,
): readonly RecipientFieldName[] {
  const missing: RecipientFieldName[] = [];
  for (const requirement of requirements.fields) {
    if (!requirement.required) continue;
    const value = values.get(requirement.name) ?? "";
    if (value.trim() === "" || recipientField(requirement.name).validate(value) !== undefined) {
      missing.push(requirement.name);
    }
  }
  return missing;
}
